import { Typography } from '@euler/app/components/typography';
import { aiColors, tint } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ApdmImportColumnChoice, ApdmReadingGap } from '@euler/model/apdm';
import { Pressable, View } from 'react-native';
import { readingGapLabel } from '../../functions/readingGapLabel';

/** Which column holds one missing field, among the columns that fit it. */
export function GapAnswer({
  gap,
  columns,
  value,
  disabled,
  onChange,
}: {
  gap: ApdmReadingGap;
  columns: ApdmImportColumnChoice[];
  value: number | undefined;
  disabled: boolean;
  onChange: (columnIndex: number) => void;
}) {
  const { rpx } = useResponsiveContext();
  return (
    <View style={{ gap: rpx(8) }}>
      <Typography.Text medium size={rpx(14)} color={aiColors.foreground}>
        {readingGapLabel(gap)}在哪一列？
      </Typography.Text>
      <View accessibilityRole="radiogroup" style={{ gap: rpx(8) }}>
        {columns.map(column => (
          <ColumnOption
            key={column.columnIndex}
            column={column}
            selected={column.columnIndex === value}
            disabled={disabled}
            onPress={() => onChange(column.columnIndex)}
          />
        ))}
      </View>
    </View>
  );
}

function ColumnOption({
  column,
  selected,
  disabled,
  onPress,
}: {
  column: ApdmImportColumnChoice;
  selected: boolean;
  disabled: boolean;
  onPress: () => void;
}) {
  const { rpx } = useResponsiveContext();
  const title = column.header || `第 ${column.columnIndex + 1} 列`;
  return (
    <Pressable
      disabled={disabled}
      accessibilityRole="radio"
      accessibilityState={{ checked: selected, disabled }}
      accessibilityLabel={`${title}，第 ${column.columnIndex + 1} 列`}
      onPress={onPress}
      style={({ pressed }) => ({
        flexDirection: 'row',
        alignItems: 'center',
        gap: rpx(10),
        paddingVertical: rpx(10),
        paddingHorizontal: rpx(12),
        borderRadius: rpx(12),
        borderWidth: selected ? 2 : 1,
        borderColor: selected ? aiColors.primary : aiColors.border,
        backgroundColor: selected
          ? tint(aiColors.primary, 0.05)
          : aiColors.card,
        opacity: pressed ? 0.85 : 1,
      })}
    >
      <View
        style={{
          width: rpx(18),
          height: rpx(18),
          borderRadius: rpx(9),
          borderWidth: 2,
          borderColor: selected ? aiColors.primary : aiColors.border,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {selected && (
          <View
            style={{
              width: rpx(8),
              height: rpx(8),
              borderRadius: rpx(4),
              backgroundColor: aiColors.primary,
            }}
          />
        )}
      </View>
      <View style={{ flex: 1, gap: rpx(2) }}>
        <Typography.Text medium size={rpx(14)} color={aiColors.foreground}>
          {title}
        </Typography.Text>
        <Typography.Text
          size={rpx(12)}
          numberOfLines={1}
          color={aiColors.mutedForeground}
        >
          {column.examples.join('　') || '（空列）'}
        </Typography.Text>
      </View>
    </Pressable>
  );
}
